export interface MotionSample {
  x: number;
  y: number;
  time: number;
}

export interface Velocity {
  x: number;
  y: number;
}

export interface RimCrossing {
  previousX: number;
  previousY: number;
  x: number;
  y: number;
  velocityY: number;
  rimX: number;
  rimY: number;
  innerWidth: number;
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function calculateThrowVelocity(
  samples: readonly MotionSample[],
  maxSpeed: number,
  windowMs = 110,
): Velocity {
  if (samples.length < 2) return { x: 0, y: 0 };

  const newest = samples.at(-1);
  if (!newest) return { x: 0, y: 0 };

  let oldest = samples[0];
  for (let index = samples.length - 2; index >= 0; index -= 1) {
    const candidate = samples[index];
    if (!candidate) continue;
    oldest = candidate;
    if (newest.time - candidate.time >= windowMs) break;
  }

  if (!oldest) return { x: 0, y: 0 };
  const elapsedSeconds = Math.max((newest.time - oldest.time) / 1000, 1 / 240);
  let x = (newest.x - oldest.x) / elapsedSeconds;
  let y = (newest.y - oldest.y) / elapsedSeconds;
  const speed = Math.hypot(x, y);

  if (speed > maxSpeed) {
    const scale = maxSpeed / speed;
    x *= scale;
    y *= scale;
  }

  return { x, y };
}

export function crossedRim(input: RimCrossing): boolean {
  if (input.velocityY <= 0 || input.previousY > input.rimY || input.y < input.rimY) return false;

  const verticalTravel = input.y - input.previousY;
  const progress = verticalTravel === 0 ? 1 : (input.rimY - input.previousY) / verticalTravel;
  const crossingX = input.previousX + (input.x - input.previousX) * progress;
  return Math.abs(crossingX - input.rimX) < input.innerWidth / 2;
}

export function reflectVelocity(
  velocity: Velocity,
  normal: Velocity,
  restitution: number,
): Velocity {
  const normalLength = Math.hypot(normal.x, normal.y) || 1;
  const nx = normal.x / normalLength;
  const ny = normal.y / normalLength;
  const projection = velocity.x * nx + velocity.y * ny;

  if (projection >= 0) return velocity;

  return {
    x: velocity.x - (1 + restitution) * projection * nx,
    y: velocity.y - (1 + restitution) * projection * ny,
  };
}
